import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { SEC, severityColor } from './SecurityTheme';
import {
  getInvestigation,
  getInvestigationReport,
  getInvestigationFindings,
  completeInvestigation,
  sendInvestigationSummary,
  getAttackerContacts,
  InvestigationItem,
  InvestigationFindingItem,
} from '../../services/api';
import { format, parseISO } from 'date-fns';
import {
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Clock,
  Activity,
  Beaker,
  XCircle,
  FileText,
  Download,
  Printer,
  RefreshCw,
  Shield,
  Users,
  Globe,
  Calendar,
  Crosshair,
  Eye,
  X,
  Mail,
  Send,
  ChevronDown,
  Copy,
  ExternalLink,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';

const STATUS_CONFIG: Record<string, { icon: React.ComponentType<any>; color: string; label: string }> = {
  pending: { icon: Clock, color: SEC.t2, label: 'Pending' },
  collecting: { icon: Activity, color: SEC.cyan, label: 'Collecting Data' },
  analyzing: { icon: Beaker, color: SEC.amber, label: 'Analyzing' },
  complete: { icon: CheckCircle2, color: SEC.green, label: 'Complete' },
  failed: { icon: XCircle, color: SEC.red, label: 'Failed' },
};

const TEMPLATE_LABELS: Record<string, string> = {
  bec: 'BEC Investigation',
  account_takeover: 'Account Takeover',
  phishing_response: 'Phishing Response',
  insider_threat: 'Insider Threat',
  post_breach: 'Post-Breach Cleanup',
  health_check: 'Quick Health Check',
};

const IOC_LABELS: Record<string, string> = {
  suspicious_inbox_rule: 'Suspicious Inbox Rule',
  foreign_sign_in: 'Foreign Sign-In',
  legacy_auth: 'Legacy Auth Protocol',
  risky_sign_in: 'Risky Sign-In',
  external_forwarding: 'External Forwarding',
  no_mfa: 'No MFA Configured',
  password_reset: 'Password/MFA Reset',
  known_attacker_ip: 'Known Attacker IP',
  attacker_sent_email: 'Attacker Sent Email',
  oauth_app_consent: 'OAuth App Consent',
  mass_rule_cleanup: 'Mass Rule Cleanup',
  cross_user_ip_correlation: 'Cross-User IP Correlation',
  privileged_role: 'Privileged Admin Role',
  admin_role_change: 'Admin Role Change',
  conditional_access_change: 'CA Policy Change',
  suspicious_mfa_registration: 'Suspicious MFA Registration',
  mailbox_delegation_change: 'Mailbox Delegation Change',
  license_change: 'License Change',
  app_registration: 'App Registration',
};

export default function InvestigationDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';

  const [investigation, setInvestigation] = useState<InvestigationItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Report state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Email summary state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Affected contacts state
  const [showAffectedContacts, setShowAffectedContacts] = useState(false);
  const [affectedContactsLoading, setAffectedContactsLoading] = useState(false);
  const [affectedContactsLoaded, setAffectedContactsLoaded] = useState(false);
  const [externalContacts, setExternalContacts] = useState<{ recipient: string; count: number; subjects: string[]; dateRange: string }[]>([]);
  const [copiedRecipient, setCopiedRecipient] = useState<string | null>(null);

  // Findings state
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [findingsLoaded, setFindingsLoaded] = useState(false);
  const [findingsData, setFindingsData] = useState<{
    findings: InvestigationFindingItem[];
    bySeverity: { critical: number; warning: number; info: number };
    byStatus: { new: number; resolved: number; false_positive: number };
    overallStatus: 'compromised' | 'at_risk' | 'clean';
    total: number;
  } | null>(null);
  const [findingsExpanded, setFindingsExpanded] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<'critical' | 'warning' | 'info' | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Group findings by user, applying severity filter
  const groupedFindings = useMemo(() => {
    if (!findingsData) return [];
    const filtered = severityFilter
      ? findingsData.findings.filter(f => f.severity === severityFilter)
      : findingsData.findings;
    const groups = new Map<string, InvestigationFindingItem[]>();
    for (const f of filtered) {
      const key = f.userId || 'Unknown User';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(f);
    }
    // Sort groups: most critical findings first
    return Array.from(groups.entries()).sort((a, b) => {
      const aCrit = a[1].filter(f => f.severity === 'critical').length;
      const bCrit = b[1].filter(f => f.severity === 'critical').length;
      if (aCrit !== bCrit) return bCrit - aCrit;
      const aWarn = a[1].filter(f => f.severity === 'warning').length;
      const bWarn = b[1].filter(f => f.severity === 'warning').length;
      return bWarn - aWarn;
    });
  }, [findingsData, severityFilter]);

  const toggleUserExpanded = (upn: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(upn)) next.delete(upn); else next.add(upn);
      return next;
    });
  };

  const handleSeverityCardClick = (sev: 'critical' | 'warning' | 'info') => {
    setSeverityFilter(prev => prev === sev ? null : sev);
    // Collapse all user groups when changing filter
    setExpandedUsers(new Set());
  };

  // Load findings when investigation is complete
  useEffect(() => {
    if (investigation?.status === 'complete' && id && clientId && !findingsLoaded && !findingsLoading) {
      setFindingsLoading(true);
      getInvestigationFindings(id, clientId)
        .then(res => {
          if (res.success) {
            setFindingsData({
              findings: res.findings,
              bySeverity: res.bySeverity,
              byStatus: res.byStatus,
              overallStatus: res.overallStatus,
              total: res.total,
            });
          }
        })
        .catch(() => { /* silent */ })
        .finally(() => {
          setFindingsLoading(false);
          setFindingsLoaded(true);
        });
    }
  }, [investigation?.status, id, clientId, findingsLoaded, findingsLoading]);

  // Load attacker contacts from dedicated API (infers attacker IPs from forensic data)
  useEffect(() => {
    if (investigation?.status === 'complete' && id && clientId && !affectedContactsLoaded && !affectedContactsLoading) {
      setAffectedContactsLoading(true);
      getAttackerContacts(id, clientId)
        .then(res => {
          if (res.success && res.contacts.length > 0) {
            setExternalContacts(res.contacts.map(c => ({
              recipient: c.recipient,
              count: c.count,
              subjects: c.subjects,
              dateRange: c.dates && c.dates.length > 0
                ? `${format(parseISO(c.dates.sort()[0]), 'MMM d')} - ${format(parseISO(c.dates.sort()[c.dates.length - 1]), 'MMM d, yyyy')}`
                : '',
            })));
          }
        })
        .catch(() => { /* silent */ })
        .finally(() => {
          setAffectedContactsLoading(false);
          setAffectedContactsLoaded(true);
        });
    }
  }, [investigation?.status, id, clientId, affectedContactsLoaded, affectedContactsLoading]);

  const generateNotificationText = (contact: { recipient: string; subjects: string[] }) => {
    if (!investigation) return '';
    const dateStart = investigation.dateRangeStart ? format(parseISO(investigation.dateRangeStart), 'MMMM d, yyyy') : '';
    const dateEnd = investigation.dateRangeEnd ? format(parseISO(investigation.dateRangeEnd), 'MMMM d, yyyy') : '';
    const subjectLines = contact.subjects.map(s => `  - Subject: "${s}"`).join('\n');
    return `Dear [Recipient Name],

We are writing to notify you of a confirmed cybersecurity incident that affected communications between our organizations.

Between ${dateStart} and ${dateEnd}, an unauthorized party who had compromised one of our email accounts sent ${contact.subjects.length} email(s) to your organization:

${subjectLines}

These emails did not originate from any authorized representative of ${investigation.clientName}.

WHAT YOU SHOULD DO:
- Treat any emails with the above subject lines as fraudulent
- If any payments, wire transfers, or approvals were initiated in response, verify and reverse them immediately
- If anyone clicked links or opened attachments, have IT review those devices

ACTIONS WE HAVE TAKEN:
- All compromised accounts have been secured and passwords reset
- All malicious inbox rules have been removed
- Multi-factor authentication has been enforced
- A full forensic investigation has been completed

We sincerely apologize for any concern or disruption. Please don't hesitate to contact us directly with any questions.

Sincerely,
[Your Name]
${investigation.clientName}`;
  };

  const handleCopyNotification = async (contact: { recipient: string; subjects: string[] }) => {
    const text = generateNotificationText(contact);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRecipient(contact.recipient);
      setTimeout(() => setCopiedRecipient(null), 2000);
    } catch {
      // Fallback for iframe context
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedRecipient(contact.recipient);
      setTimeout(() => setCopiedRecipient(null), 2000);
    }
  };

  const handleSendSummary = async () => {
    if (!id || !clientId || !emailRecipient) return;
    setSendingEmail(true);
    setEmailError(null);
    setEmailSuccess(null);
    try {
      const resp = await sendInvestigationSummary(id, {
        clientId,
        recipientEmail: emailRecipient,
        recipientName: emailRecipientName || undefined,
      });
      if (resp.success) {
        setEmailSuccess(resp.message || `Summary sent to ${emailRecipient}`);
        setTimeout(() => {
          setShowEmailDialog(false);
          setEmailSuccess(null);
          setEmailRecipient('');
          setEmailRecipientName('');
        }, 2000);
        // Refresh to get updated timeline
        fetchData();
      } else {
        setEmailError((resp as any).error || 'Failed to send');
      }
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to send summary');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!id || !clientId) return;
    try {
      setError(null);
      const resp = await getInvestigation(id, clientId);
      if (resp.success) {
        setInvestigation(resp.investigation);

        // Stop polling if complete or failed
        if (['complete', 'failed'].includes(resp.investigation.status)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load investigation');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-poll if still in progress
  useEffect(() => {
    if (investigation && !['complete', 'failed'].includes(investigation.status)) {
      pollRef.current = setInterval(fetchData, 5000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [investigation?.status, fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleGenerateReport = async () => {
    if (!id || !clientId) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const resp = await getInvestigationReport(id, clientId);
      if (resp.success) {
        setReportHtml(resp.html);
        setShowReportModal(true);
      } else {
        setReportError('Failed to generate report');
      }
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handlePrintReport = () => {
    if (!reportHtml) return;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(reportHtml);
      win.document.close();
      setTimeout(() => win.print(), 500);
    }
  };

  const handleDownloadReport = () => {
    if (!reportHtml) return;
    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investigation-report-${id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: SEC.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: SEC.t2 }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
          Loading investigation...
        </div>
      </div>
    );
  }

  if (error || !investigation) {
    return (
      <div style={{ minHeight: '100vh', background: SEC.bg, padding: 24 }}>
        <Link to="/security/investigations" style={{ display: 'flex', alignItems: 'center', gap: 6, color: SEC.cyan, textDecoration: 'none', marginBottom: 16 }}>
          <ChevronLeft size={14} /> Back to Investigations
        </Link>
        <div style={{
          padding: '16px 20px', borderRadius: 8,
          background: 'rgba(239,68,68,0.1)', border: `1px solid ${SEC.red}`,
          color: SEC.red, fontSize: 14,
        }}>
          {error || 'Investigation not found'}
        </div>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[investigation.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConf.icon;

  // AI analysis threat level
  const threatLevel = investigation.aiAnalysis?.threatLevel;
  const threatColor = threatLevel === 'critical' ? SEC.red : threatLevel === 'high' ? SEC.red : threatLevel === 'medium' ? SEC.amber : threatLevel === 'low' ? SEC.green : SEC.cyan;

  return (
    <div style={{ minHeight: '100vh', background: SEC.bg, color: SEC.t1, padding: '24px' }}>
      {/* Back link */}
      <Link
        to="/security/investigations"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: SEC.cyan, textDecoration: 'none', marginBottom: 16, fontSize: 13 }}
      >
        <ChevronLeft size={14} /> Back to Investigations
      </Link>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 9999,
              background: `${statusConf.color}18`, color: statusConf.color,
              fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            }}>
              <StatusIcon size={14} />
              {statusConf.label}
            </span>
            {investigation.templateId && (
              <span style={{
                padding: '4px 10px', borderRadius: 9999,
                background: SEC.cyanGlow, color: SEC.cyan,
                fontSize: 11, fontWeight: 500,
              }}>
                {TEMPLATE_LABELS[investigation.templateId] || investigation.templateId}
              </span>
            )}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: SEC.t1, margin: '0 0 4px' }}>{investigation.title}</h1>
          {investigation.description && (
            <p style={{ fontSize: 13, color: SEC.t2, margin: 0 }}>{investigation.description}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6,
              background: SEC.panel2, border: `1px solid ${SEC.border}`,
              color: SEC.t2, cursor: 'pointer', fontSize: 13,
            }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setShowEmailDialog(true); setEmailError(null); setEmailSuccess(null); }}
            disabled={investigation.status !== 'complete'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 6,
              background: investigation.status === 'complete' ? SEC.panel2 : SEC.t3,
              border: `1px solid ${investigation.status === 'complete' ? SEC.border : 'transparent'}`,
              color: investigation.status === 'complete' ? SEC.t1 : SEC.t3,
              cursor: investigation.status === 'complete' ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 600,
              opacity: investigation.status === 'complete' ? 1 : 0.5,
            }}
          >
            <Mail size={14} /> Email Summary
          </button>
          <button
            onClick={handleGenerateReport}
            disabled={reportLoading || investigation.status !== 'complete'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 6,
              background: investigation.status === 'complete' ? SEC.cyan : SEC.t3,
              border: 'none', color: '#000',
              cursor: investigation.status === 'complete' ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 600,
              opacity: investigation.status === 'complete' ? 1 : 0.5,
            }}
          >
            {reportLoading ? (
              <><Loader2 size={14} className="animate-spin" /> Generating...</>
            ) : (
              <><FileText size={14} /> Generate Report</>
            )}
          </button>
        </div>
      </div>

      {/* Report error */}
      {reportError && (
        <div style={{
          padding: '10px 14px', borderRadius: 6, marginBottom: 16,
          background: 'rgba(239,68,68,0.1)', border: `1px solid ${SEC.red}`,
          color: SEC.red, fontSize: 13,
        }}>
          {reportError}
        </div>
      )}

      {/* Metadata cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 24 }}>
        <MetaCard icon={Shield} label="Client" value={investigation.clientName} />
        <MetaCard icon={Globe} label="Tenant" value={investigation.tenantDomain} mono />
        <MetaCard icon={Users} label="Target Users" value={investigation.targetUsers.length === 0 ? 'All (auto-discover)' : String(investigation.targetUsers.length)} />
        <MetaCard icon={Calendar} label="Date Range" value={`${format(parseISO(investigation.dateRangeStart), 'MMM d')} - ${format(parseISO(investigation.dateRangeEnd), 'MMM d, yyyy')}`} />
        <MetaCard icon={Crosshair} label="Data Sources" value={String(investigation.dataSources.length)} />
        <MetaCard icon={Eye} label="IOC Checks" value={String(investigation.iocCategories.length)} />
        <MetaCard icon={Clock} label="Started" value={investigation.startedAt ? format(parseISO(investigation.startedAt), 'MMM d, h:mm a') : 'N/A'} />
        <MetaCard icon={Clock} label="Completed" value={investigation.completedAt ? format(parseISO(investigation.completedAt), 'MMM d, h:mm a') : 'In progress...'} />
      </div>

      {/* Progress bar for active investigations */}
      {['collecting', 'analyzing', 'pending'].includes(investigation.status) && (
        <div style={{
          padding: '16px 20px', borderRadius: 8, marginBottom: 24,
          background: SEC.panel, border: `1px solid ${SEC.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Activity size={16} style={{ color: SEC.cyan }} className="animate-pulse" />
            <span style={{ color: SEC.cyan, fontWeight: 600, fontSize: 14 }}>Collection in progress...</span>
          </div>
          <div style={{
            height: 4, borderRadius: 2, background: SEC.panel2,
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: `linear-gradient(90deg, ${SEC.cyan}, ${SEC.cyanDim})`,
              width: investigation.status === 'analyzing' ? '80%' : investigation.status === 'collecting' ? '40%' : '10%',
              transition: 'width 0.5s ease',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <p style={{ color: SEC.t3, fontSize: 12, margin: 0 }}>
              Auto-refreshing every 5 seconds. Latest event: {investigation.timeline.length > 0 ? investigation.timeline[investigation.timeline.length - 1].event : 'Waiting...'}
            </p>
            {investigation.startedAt && (Date.now() - new Date(investigation.startedAt).getTime() > 5 * 60 * 1000) && (
              <button
                onClick={async () => {
                  if (!confirm('Mark this investigation as complete? You can generate a report with whatever data has been collected so far.')) return;
                  setCompleting(true);
                  try {
                    const resp = await completeInvestigation(investigation.id, clientId!);
                    if (resp.success) {
                      setInvestigation({ ...investigation, status: 'complete', completedAt: new Date().toISOString() });
                    }
                  } catch { /* ignore */ } finally { setCompleting(false); }
                }}
                disabled={completing}
                style={{
                  padding: '4px 12px', borderRadius: 4,
                  background: 'rgba(234,179,8,0.15)', border: `1px solid ${SEC.amber}`,
                  color: SEC.amber, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {completing ? 'Completing...' : 'Mark Complete'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* AI Analysis Summary */}
      {investigation.aiAnalysis && (
        <div style={{
          padding: '16px 20px', borderRadius: 8, marginBottom: 24,
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderLeft: `4px solid ${threatColor}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Beaker size={16} style={{ color: threatColor }} />
            <span style={{ color: threatColor, fontWeight: 700, fontSize: 16, textTransform: 'uppercase' }}>
              Threat Level: {threatLevel || 'N/A'}
            </span>
            <span style={{ fontSize: 12, color: SEC.t3, marginLeft: 'auto' }}>
              Confidence: {Math.round((investigation.aiAnalysis.confidence || 0) * 100)}%
            </span>
          </div>
          <p style={{ color: SEC.t2, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            {investigation.aiAnalysis.summary}
          </p>
        </div>
      )}

      {/* Findings Summary — shown when investigation is complete */}
      {investigation.status === 'complete' && (
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 24,
          borderLeft: `4px solid ${
            findingsData?.overallStatus === 'compromised' ? SEC.red
            : findingsData?.overallStatus === 'at_risk' ? SEC.amber
            : SEC.green
          }`,
        }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setFindingsExpanded(!findingsExpanded)}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, color: SEC.t1, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={16} style={{ color: SEC.cyan }} />
              Findings Summary
              {findingsData && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 10px', borderRadius: 9999, marginLeft: 8,
                  fontSize: 11, fontWeight: 600,
                  background: findingsData.overallStatus === 'compromised' ? 'rgba(239,68,68,0.15)' :
                    findingsData.overallStatus === 'at_risk' ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                  color: findingsData.overallStatus === 'compromised' ? SEC.red :
                    findingsData.overallStatus === 'at_risk' ? SEC.amber : SEC.green,
                }}>
                  {findingsData.overallStatus === 'compromised' ? <><ShieldAlert size={12} /> Compromised</> :
                   findingsData.overallStatus === 'at_risk' ? <><AlertTriangle size={12} /> At Risk</> :
                   <><ShieldCheck size={12} /> Clean</>}
                </span>
              )}
            </h3>
            <ChevronDown
              size={16}
              style={{
                color: SEC.t3,
                transform: findingsExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </div>

          {findingsLoading ? (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, color: SEC.t3, fontSize: 13 }}>
              <Loader2 size={14} className="animate-spin" /> Loading findings...
            </div>
          ) : findingsData && findingsExpanded && (
            <div style={{ marginTop: 16 }}>
              {/* Clickable severity filter cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
                {([
                  { key: 'critical' as const, count: findingsData.bySeverity.critical, color: SEC.red, bgActive: 'rgba(239,68,68,0.15)', borderActive: 'rgba(239,68,68,0.5)' },
                  { key: 'warning' as const, count: findingsData.bySeverity.warning, color: SEC.amber, bgActive: 'rgba(245,158,11,0.15)', borderActive: 'rgba(245,158,11,0.5)' },
                  { key: 'info' as const, count: findingsData.bySeverity.info, color: SEC.cyan, bgActive: 'rgba(88,166,255,0.15)', borderActive: 'rgba(88,166,255,0.5)' },
                ]).map(card => {
                  const isActive = severityFilter === card.key;
                  const hasFindings = card.count > 0;
                  return (
                    <div
                      key={card.key}
                      onClick={() => hasFindings && handleSeverityCardClick(card.key)}
                      style={{
                        padding: '12px 16px', borderRadius: 8, textAlign: 'center',
                        cursor: hasFindings ? 'pointer' : 'default',
                        background: isActive ? card.bgActive : hasFindings ? `${card.color}0a` : SEC.panel2,
                        border: `2px solid ${isActive ? card.borderActive : hasFindings ? `${card.color}30` : SEC.border}`,
                        transition: 'all 0.2s ease',
                        transform: isActive ? 'scale(1.02)' : 'none',
                        boxShadow: isActive ? `0 0 12px ${card.color}20` : 'none',
                      }}
                    >
                      <div style={{ fontSize: 26, fontWeight: 700, color: hasFindings ? card.color : SEC.t3 }}>
                        {card.count}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: isActive ? card.color : SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {card.key}
                      </div>
                    </div>
                  );
                })}
                <div
                  onClick={() => { setSeverityFilter(null); setExpandedUsers(new Set()); }}
                  style={{
                    padding: '12px 16px', borderRadius: 8, textAlign: 'center',
                    cursor: 'pointer',
                    background: severityFilter === null ? 'rgba(255,255,255,0.05)' : SEC.panel2,
                    border: `2px solid ${severityFilter === null ? 'rgba(255,255,255,0.2)' : SEC.border}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ fontSize: 26, fontWeight: 700, color: SEC.t1 }}>
                    {findingsData.total}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: severityFilter === null ? SEC.t1 : SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total
                  </div>
                </div>
              </div>

              {/* Active filter indicator */}
              {severityFilter && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                  padding: '6px 12px', borderRadius: 6,
                  background: SEC.panel2, fontSize: 12, color: SEC.t2,
                }}>
                  Showing <span style={{
                    color: severityFilter === 'critical' ? SEC.red : severityFilter === 'warning' ? SEC.amber : SEC.cyan,
                    fontWeight: 600, textTransform: 'uppercase',
                  }}>{severityFilter}</span> findings across {groupedFindings.length} user{groupedFindings.length !== 1 ? 's' : ''}
                  <button
                    onClick={() => { setSeverityFilter(null); setExpandedUsers(new Set()); }}
                    style={{
                      marginLeft: 'auto', background: 'none', border: 'none',
                      color: SEC.t3, cursor: 'pointer', padding: '2px 4px', fontSize: 11,
                    }}
                  >
                    <X size={12} /> Clear
                  </button>
                </div>
              )}

              {/* Findings grouped by user — collapsed accordions */}
              {findingsData.findings.length === 0 ? (
                <p style={{ fontSize: 13, color: SEC.t3 }}>No findings detected during this investigation.</p>
              ) : groupedFindings.length === 0 ? (
                <p style={{ fontSize: 13, color: SEC.t3 }}>No {severityFilter} findings found.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {groupedFindings.map(([upn, findings]) => {
                    const isExpanded = expandedUsers.has(upn);
                    const userCrit = findings.filter(f => f.severity === 'critical').length;
                    const userWarn = findings.filter(f => f.severity === 'warning').length;
                    const userInfo = findings.filter(f => f.severity === 'info').length;
                    // Determine user-level severity for the accent bar
                    const userAccent = userCrit > 0 ? SEC.red : userWarn > 0 ? SEC.amber : SEC.cyan;

                    return (
                      <div key={upn} style={{
                        borderRadius: 8, overflow: 'hidden',
                        border: `1px solid ${isExpanded ? userAccent + '40' : SEC.border}`,
                        background: SEC.panel2,
                        transition: 'border-color 0.2s',
                      }}>
                        {/* User group header — always visible */}
                        <div
                          onClick={() => toggleUserExpanded(upn)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 16px', cursor: 'pointer',
                            borderLeft: `3px solid ${userAccent}`,
                            background: isExpanded ? `${userAccent}08` : 'transparent',
                            transition: 'background 0.2s',
                          }}
                        >
                          <ChevronDown
                            size={14}
                            style={{
                              color: SEC.t3, flexShrink: 0,
                              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.2s',
                            }}
                          />
                          <Users size={14} style={{ color: userAccent, flexShrink: 0 }} />
                          <span style={{ fontFamily: 'monospace', fontSize: 13, color: SEC.t1, fontWeight: 600 }}>
                            {upn}
                          </span>
                          {/* Severity mini badges */}
                          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
                            {userCrit > 0 && (
                              <span style={{
                                padding: '1px 8px', borderRadius: 9999,
                                background: 'rgba(239,68,68,0.15)', color: SEC.red,
                                fontSize: 10, fontWeight: 700,
                              }}>
                                {userCrit} critical
                              </span>
                            )}
                            {userWarn > 0 && (
                              <span style={{
                                padding: '1px 8px', borderRadius: 9999,
                                background: 'rgba(245,158,11,0.15)', color: SEC.amber,
                                fontSize: 10, fontWeight: 700,
                              }}>
                                {userWarn} warning
                              </span>
                            )}
                            {userInfo > 0 && (
                              <span style={{
                                padding: '1px 8px', borderRadius: 9999,
                                background: 'rgba(88,166,255,0.15)', color: SEC.cyan,
                                fontSize: 10, fontWeight: 700,
                              }}>
                                {userInfo} info
                              </span>
                            )}
                            <span style={{ fontSize: 11, color: SEC.t3, marginLeft: 4 }}>
                              {findings.length} finding{findings.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Expanded: individual finding cards */}
                        {isExpanded && (
                          <div style={{ padding: '8px 16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {findings.map((f, idx) => {
                              const sevColor = f.severity === 'critical' ? SEC.red : f.severity === 'warning' ? SEC.amber : SEC.cyan;
                              return (
                                <div key={f.id || idx} style={{
                                  padding: '10px 14px', borderRadius: 6,
                                  background: SEC.bg, border: `1px solid ${SEC.border}`,
                                  borderLeft: `3px solid ${sevColor}`,
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <span style={{
                                      padding: '1px 8px', borderRadius: 9999,
                                      background: `${sevColor}18`, color: sevColor,
                                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                                    }}>
                                      {f.severity}
                                    </span>
                                    <span style={{ color: SEC.t1, fontWeight: 600, fontSize: 13 }}>
                                      {IOC_LABELS[f.type] || f.type}
                                    </span>
                                    {f.detectedAt && (
                                      <span style={{ fontSize: 10, color: SEC.t3, marginLeft: 'auto' }}>
                                        {(() => { try { return format(parseISO(f.detectedAt), 'MMM d, h:mm a'); } catch { return f.detectedAt; } })()}
                                      </span>
                                    )}
                                  </div>
                                  <p style={{ fontSize: 12, color: SEC.t2, margin: 0, lineHeight: 1.5 }}>
                                    {f.summary}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Timeline */}
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px',
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: SEC.cyan, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={14} /> Timeline ({investigation.timeline.length})
          </h3>
          {investigation.timeline.length === 0 ? (
            <p style={{ color: SEC.t3, fontSize: 13 }}>No events yet</p>
          ) : (
            <div style={{ position: 'relative', paddingLeft: 20 }}>
              <div style={{ position: 'absolute', left: 5, top: 0, bottom: 0, width: 2, background: SEC.border }} />
              {investigation.timeline.slice().reverse().map((evt, i) => (
                <div key={i} style={{ position: 'relative', paddingBottom: 16, paddingLeft: 16 }}>
                  <div style={{
                    position: 'absolute', left: -18, top: 4,
                    width: 10, height: 10, borderRadius: '50%',
                    border: `2px solid ${severityColor(evt.severity)}`,
                    background: SEC.bg,
                  }} />
                  <div style={{ fontSize: 11, color: SEC.t3, fontFamily: 'monospace' }}>
                    {format(parseISO(evt.date), 'MMM d, h:mm:ss a')}
                  </div>
                  <div style={{ fontSize: 13, color: SEC.t1, marginTop: 2 }}>{evt.event}</div>
                  {evt.user && (
                    <div style={{ fontSize: 11, color: SEC.cyan, marginTop: 1 }}>{evt.user}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Target Users */}
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px',
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: SEC.cyan, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={14} /> Target Users ({investigation.targetUsers.length === 0 ? 'All' : investigation.targetUsers.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {investigation.targetUsers.length === 0 ? (
              <div style={{
                padding: '10px 14px', background: SEC.cyanGlow,
                border: `1px solid ${SEC.cyan}`, borderRadius: 6,
                fontSize: 12, color: SEC.cyan, fontWeight: 500,
              }}>
                {['collecting', 'analyzing', 'pending'].includes(investigation.status)
                  ? 'Auto-discovering all licensed users...'
                  : 'All licensed users (auto-discovered)'}
              </div>
            ) : (
              investigation.targetUsers.map(upn => (
                <div key={upn} style={{
                  padding: '8px 12px', background: SEC.panel2,
                  border: `1px solid ${SEC.border}`, borderRadius: 6,
                  fontFamily: 'monospace', fontSize: 12, color: SEC.t1,
                }}>
                  {upn}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* IOC Categories */}
      {investigation.iocCategories.length > 0 && (
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 24,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: SEC.cyan, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Crosshair size={14} /> IOC Categories Checked
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {investigation.iocCategories.map(ioc => (
              <span key={ioc} style={{
                padding: '4px 10px', borderRadius: 9999,
                background: SEC.cyanGlow, color: SEC.cyan,
                fontSize: 11, fontWeight: 500,
              }}>
                {IOC_LABELS[ioc] || ioc}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Known Attacker IPs */}
      {investigation.knownAttackerIPs.length > 0 && (
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 24,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: SEC.red, marginBottom: 12 }}>
            Known Attacker IPs
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {investigation.knownAttackerIPs.map(ip => (
              <span key={ip} style={{
                padding: '4px 10px', borderRadius: 4,
                background: 'rgba(239,68,68,0.1)', color: SEC.red,
                fontSize: 12, fontFamily: 'monospace',
              }}>
                {ip}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Affected External Contacts — always show for complete investigations */}
      {investigation.status === 'complete' && (
        <div style={{
          background: SEC.panel, border: `1px solid ${SEC.border}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 24,
          borderLeft: `4px solid ${externalContacts.length > 0 ? SEC.amber : SEC.border}`,
        }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: externalContacts.length > 0 ? 'pointer' : 'default' }}
            onClick={() => externalContacts.length > 0 && setShowAffectedContacts(!showAffectedContacts)}
          >
            <h3 style={{ fontSize: 14, fontWeight: 600, color: externalContacts.length > 0 ? SEC.amber : SEC.t3, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExternalLink size={14} />
              Affected External Contacts {externalContacts.length > 0 ? `(${externalContacts.length})` : ''}
            </h3>
            {externalContacts.length > 0 && (
              <ChevronDown
                size={16}
                style={{
                  color: SEC.t3,
                  transform: showAffectedContacts ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            )}
          </div>

          {affectedContactsLoading ? (
            <p style={{ fontSize: 12, color: SEC.t3, margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              Loading attacker email data...
            </p>
          ) : externalContacts.length === 0 ? (
            <div style={{ marginTop: 8 }}>
              <p style={{ fontSize: 12, color: SEC.t3, margin: 0 }}>
                No external recipients detected from attacker emails.
              </p>
              <p style={{ fontSize: 11, color: SEC.t4, margin: '6px 0 0', lineHeight: 1.5 }}>
                This investigation collected <strong style={{ color: SEC.t3 }}>0 message traces</strong> from Exchange Online.
                Message trace data is required to identify who the attacker emailed from compromised accounts.
                To get this data, run a new investigation with message trace collection enabled, or check that the
                EXO runbook has the correct permissions to pull message trace data.
              </p>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 12, color: SEC.t3, margin: '4px 0 0' }}>
                External recipients who received fraudulent emails from compromised accounts
              </p>

              {showAffectedContacts && (
                <div style={{ marginTop: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${SEC.border}` }}>
                        <th style={{ textAlign: 'left', padding: '8px 10px', color: SEC.t3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>External Recipient</th>
                        <th style={{ textAlign: 'center', padding: '8px 10px', color: SEC.t3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Emails</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', color: SEC.t3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subjects</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', color: SEC.t3, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Range</th>
                        <th style={{ textAlign: 'right', padding: '8px 10px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {externalContacts.map(contact => (
                        <tr key={contact.recipient} style={{ borderBottom: `1px solid ${SEC.border}` }}>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: SEC.t1 }}>
                            {contact.recipient}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: SEC.t2 }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
                              background: 'rgba(239,68,68,0.1)', color: SEC.red,
                              fontWeight: 600, fontSize: 11,
                            }}>
                              {contact.count}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', color: SEC.t2, maxWidth: 250 }}>
                            {contact.subjects.slice(0, 2).map((s, i) => (
                              <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                &ldquo;{s}&rdquo;
                              </div>
                            ))}
                            {contact.subjects.length > 2 && (
                              <div style={{ color: SEC.t4, fontSize: 11 }}>+{contact.subjects.length - 2} more</div>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', color: SEC.t3, whiteSpace: 'nowrap', fontSize: 11 }}>
                            {contact.dateRange}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                            <button
                              onClick={() => handleCopyNotification(contact)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '4px 10px', borderRadius: 4,
                                background: copiedRecipient === contact.recipient ? `${SEC.green}15` : SEC.panel2,
                                border: `1px solid ${copiedRecipient === contact.recipient ? SEC.green : SEC.border}`,
                                color: copiedRecipient === contact.recipient ? SEC.green : SEC.t2,
                                cursor: 'pointer', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                              }}
                            >
                              {copiedRecipient === contact.recipient ? (
                                <><CheckCircle2 size={12} /> Copied!</>
                              ) : (
                                <><Copy size={12} /> Copy Notification</>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Email Summary Dialog */}
      {showEmailDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div
            onClick={() => setShowEmailDialog(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
          />
          <div style={{
            position: 'relative', zIndex: 1,
            width: '100%', maxWidth: 440,
            background: SEC.panel, border: `1px solid ${SEC.border}`,
            borderRadius: 12, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: `1px solid ${SEC.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail size={16} style={{ color: SEC.cyan }} />
                <span style={{ fontWeight: 600, color: SEC.t1, fontSize: 14 }}>Email Client Summary</span>
              </div>
              <button
                onClick={() => setShowEmailDialog(false)}
                style={{ background: 'none', border: 'none', color: SEC.t3, cursor: 'pointer', padding: 4 }}
              >
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: 13, color: SEC.t2, marginBottom: 16, lineHeight: 1.5 }}>
                Send a plain-English security summary to the client. No technical jargon — just what happened, who was affected, and what to do next.
              </p>

              {emailSuccess && (
                <div style={{
                  padding: '10px 14px', borderRadius: 6, marginBottom: 12,
                  background: 'rgba(34,197,94,0.1)', border: `1px solid ${SEC.green}`,
                  color: SEC.green, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <CheckCircle2 size={14} /> {emailSuccess}
                </div>
              )}
              {emailError && (
                <div style={{
                  padding: '10px 14px', borderRadius: 6, marginBottom: 12,
                  background: 'rgba(239,68,68,0.1)', border: `1px solid ${SEC.red}`,
                  color: SEC.red, fontSize: 13,
                }}>
                  {emailError}
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: SEC.t2, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={emailRecipient}
                  onChange={e => setEmailRecipient(e.target.value)}
                  placeholder="client@company.com"
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    borderRadius: 6, color: SEC.t1, fontSize: 13, outline: 'none',
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: SEC.t2, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Recipient Name (optional)
                </label>
                <input
                  type="text"
                  value={emailRecipientName}
                  onChange={e => setEmailRecipientName(e.target.value)}
                  placeholder="John Smith"
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    borderRadius: 6, color: SEC.t1, fontSize: 13, outline: 'none',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowEmailDialog(false)}
                  style={{
                    padding: '8px 14px', borderRadius: 6,
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    color: SEC.t2, cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendSummary}
                  disabled={sendingEmail || !emailRecipient}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 6,
                    background: emailRecipient && !sendingEmail ? SEC.cyan : SEC.t3,
                    border: 'none', color: '#000',
                    cursor: emailRecipient && !sendingEmail ? 'pointer' : 'not-allowed',
                    fontSize: 13, fontWeight: 600,
                    opacity: emailRecipient && !sendingEmail ? 1 : 0.5,
                  }}
                >
                  {sendingEmail ? (
                    <><Loader2 size={14} className="animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={14} /> Send Summary</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Preview Modal */}
      {showReportModal && reportHtml && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div
            onClick={() => setShowReportModal(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' }}
          />
          <div style={{
            position: 'relative', zIndex: 1,
            width: '95%', maxWidth: 1200, height: '90vh',
            background: SEC.panel, border: `1px solid ${SEC.border}`,
            borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', borderBottom: `1px solid ${SEC.border}`,
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={16} style={{ color: SEC.cyan }} />
                <span style={{ fontWeight: 600, color: SEC.t1, fontSize: 14 }}>Investigation Report</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleDownloadReport}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 6,
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    color: SEC.t2, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  <Download size={12} /> Download
                </button>
                <button
                  onClick={handlePrintReport}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 6,
                    background: SEC.panel2, border: `1px solid ${SEC.border}`,
                    color: SEC.t2, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  <Printer size={12} /> Print
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  style={{ background: 'none', border: 'none', color: SEC.t3, cursor: 'pointer', padding: 4 }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {/* Report iframe */}
            <iframe
              srcDoc={reportHtml}
              style={{
                flex: 1, width: '100%', border: 'none',
                background: '#0a0e17',
              }}
              title="Investigation Report"
              sandbox="allow-same-origin allow-scripts"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MetaCard({ icon: Icon, label, value, mono }: { icon: React.ComponentType<any>; label: string; value: string; mono?: boolean }) {
  return (
    <div style={{
      padding: '12px 16px', background: SEC.panel,
      border: `1px solid ${SEC.border}`, borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Icon size={12} style={{ color: SEC.t3 }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: SEC.t3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
      </div>
      <div style={{
        fontSize: 13, color: SEC.t1, fontWeight: 500,
        fontFamily: mono ? "'Cascadia Code', 'Fira Code', monospace" : 'inherit',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
    </div>
  );
}
